import { useAppDispatch, useAppSelector } from 'app/store/store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GeoJsonLayer } from 'deck.gl/typed';
import { WKTLoader } from '@loaders.gl/wkt';
import { parseSync } from '@loaders.gl/core';

import i18n from 'resources/i18n';
import { useLazyGetAreaCardQuery } from 'app/api/facade';
import { hexToRGBArray, throttle } from 'app/helpers';
import { setSelectedArea } from 'app/utils/marineInfrastructureReducer';
import { useLazyGetAreasQuery } from 'app/api/marine-infrastructure';
import { useAlertHook, useZoneSelectHandler } from 'app/hooks';

export const useLayerAreas = (isAreasMode: boolean) => {
  const dispatch = useAppDispatch();
  const language = i18n.language;
  const handleZoneSelect = useZoneSelectHandler();
  const [geometryData, setGeometryData] = useState<any>();
  const { addAlert } = useAlertHook();

  const selectedArea = useAppSelector((state) => state.marineInfrastructureSlice.selectedArea);

  const [getAreas] = useLazyGetAreasQuery();
  const debouncedGetAreasHandler = useCallback(throttle(getAreas, 700), []);

  const [getAreaCard, { data, error, ...getAreaCardRequest }] = useLazyGetAreaCardQuery();
  const debouncedGetAreaCardHandler = useCallback(throttle(getAreaCard, 300), []);

  useEffect(() => {
    if (selectedArea && selectedArea.code && !('geometry' in selectedArea)) {
      debouncedGetAreaCardHandler(selectedArea.code);
    }
  }, [selectedArea]);

  useEffect(() => {
    if (error && 'status' in error && error.status === 404) {
      addAlert(error);

      dispatch(setSelectedArea(null));
      setGeometryData(null);
      debouncedGetAreasHandler({});
    }
  }, [error]);

  useEffect(() => {
    if (data?.result?.geometry) {
      dispatch(setSelectedArea(data.result));
      const geometry = parseSync(data?.result?.geometry, WKTLoader);
      setGeometryData({
        type: "FeatureCollection",
        features: [
          {
            geometry,
            properties: {
              name: data?.result?.name,
            },
          },
        ],
      });
    }
  }, [data]);

  useEffect(() => {
    if (language && !getAreaCardRequest.isUninitialized && selectedArea?.code) {
      debouncedGetAreaCardHandler(selectedArea.code);
    }
  }, [language]);

  const polygonLayer = new GeoJsonLayer({
    id: 'areas-polygon-layer',
    data: geometryData,
    filled: true,
    stroked: true,
    pickable: true,
    getPointRadius: () => 6,
    pointRadiusUnits: 'pixels',
    getFillColor: (): [number, number, number, number] => {
      const defaultFill: [number, number, number, number] = [255, 112, 112, 40];
      const defaultFillSpecial: [number, number, number, number] = [115, 233, 22, 40];

      if (data?.result?.color) {
        return hexToRGBArray(data.result.color, 0.3);
      } else {
        if (data?.result && 'subType' in data.result) {
          return defaultFillSpecial;
        }
        return defaultFill;
      }
    },
    getLineColor: () => {
      const defaultStroke: [number, number, number, number] = [239, 16, 16, 255];
      const defaultStrokeSpecial: [number, number, number, number] = [58, 182, 0, 255];

      if (data?.result?.color) {
        return hexToRGBArray(data.result.color, 0.15);
      } else {
        if (data?.result && 'subType' in data.result) {
          return defaultStrokeSpecial;
        }
        return defaultStroke;
      }
    },
    onClick: () => {
      if (data?.result) {
        handleZoneSelect(data.result);
      }
    },
    getLineWidth: 5,
    lineWidthUnits: 'pixels',
    visible: isAreasMode,
  });

  const polygonLayerBorder = new GeoJsonLayer({
    id: 'polygon-border-layer',
    data: geometryData,
    filled: false,
    getPointRadius: () => 10,
    pointRadiusUnits: 'pixels',
    stroked: true,
    getLineColor: [255, 255, 255, 190],
    getLineWidth: 2,
    lineWidthUnits: 'pixels',
    visible: isAreasMode,
  });

  const result = useMemo(
    () => ({
      layer: [polygonLayer, polygonLayerBorder],
      data: getAreaCardRequest.isSuccess ? data?.result : null,
      isLoading: getAreaCardRequest.isLoading || getAreaCardRequest.isFetching,
    }),
    [polygonLayer, polygonLayerBorder, data, getAreaCardRequest],
  );
  return result;
};
